import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;
const INTERVAL = 2000;

const fmt = (amount, currency) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase(), minimumFractionDigits: 0 }).format((amount || 0) / 100);

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [data, setData] = useState(null);
  const [log, setLog] = useState([]);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const addLog = (m) => setLog((l) => [...l, m]);

    const poll = async () => {
      attempts.current += 1;
      addLog(`> attempt ${attempts.current}: querying server...`);
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setData(data);
        if (data.payment_status === "paid") {
          addLog("> STATUS: PAID ✓");
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          addLog(`> STATUS: ${data.payment_status.toUpperCase()}`);
          setState("failed");
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          addLog("> polling timed out");
          setState("timeout");
          return;
        }
        setTimeout(poll, INTERVAL);
      } catch (e) {
        if (cancelled) return;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, INTERVAL);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-lg bg-white border-2 border-[#0A0A0A] brutal-shadow p-10" data-testid="payment-success-panel">
        {state === "polling" && (
          <div data-testid="status-polling">
            <div className="font-mono-b text-xs uppercase tracking-[0.3em] text-[#FF3B30] mb-6">[ STATUS: POLLING... ]</div>
            <h1 className="font-display font-extrabold text-3xl tracking-tighter">Confirming payment</h1>
            <p className="mt-3 text-sm text-[#0A0A0A]/70 leading-relaxed">
              We don't trust the redirect — we're verifying with our server until the payment is confirmed.
            </p>
            <div className="mt-8 border-2 border-[#0A0A0A] bg-[#F4F4F4] p-4 font-mono-b text-xs space-y-1 h-40 overflow-auto">
              {log.map((l, i) => <div key={i}>{l}</div>)}
              <div className="inline-block w-2 h-4 bg-[#FF3B30] animate-pulse" />
            </div>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="status-paid">
            <div className="w-16 h-16 border-2 border-[#0A0A0A] bg-[#22C55E] flex items-center justify-center mb-6">
              <CheckCircle2 size={36} strokeWidth={2.5} className="text-white" />
            </div>
            <div className="font-mono-b text-xs uppercase tracking-[0.3em] text-[#22C55E] mb-3">[ ACCESS UNLOCKED ]</div>
            <h1 className="font-display font-extrabold text-4xl tracking-tighter">Payment Successful</h1>
            <p className="mt-4 text-sm text-[#0A0A0A]/70 leading-relaxed">
              Your <strong>{data?.product_name}</strong> plan is active. Server-confirmed payment of{" "}
              <strong>{fmt(data?.amount, data?.currency)}</strong>. You now have full access.
            </p>
            <div className="mt-6 border-2 border-[#0A0A0A] bg-[#F4F4F4] p-4 font-mono-b text-[11px] uppercase tracking-[0.15em] break-all">
              <div className="text-[#0A0A0A]/50">Session</div>
              <div>{data?.session_id}</div>
            </div>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-[#0A0A0A] text-white font-mono-b uppercase tracking-[0.15em] text-sm px-6 py-3 border-2 border-[#0A0A0A] hover:-translate-y-1 transition-transform duration-200" data-testid="back-home-button">
              <ArrowLeft size={16} strokeWidth={2.5} /> Back to home
            </Link>
          </div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div data-testid="status-failed">
            <div className="w-16 h-16 border-2 border-[#0A0A0A] bg-[#FF3B30] flex items-center justify-center mb-6">
              <XCircle size={36} strokeWidth={2.5} className="text-white" />
            </div>
            <div className="font-mono-b text-xs uppercase tracking-[0.3em] text-[#FF3B30] mb-3">
              [ {state === "timeout" ? "STILL PENDING" : "NOT CONFIRMED"} ]
            </div>
            <h1 className="font-display font-extrabold text-3xl tracking-tighter">
              {state === "timeout" ? "Taking longer than expected" : "Payment not confirmed"}
            </h1>
            <p className="mt-4 text-sm text-[#0A0A0A]/70 leading-relaxed">
              {state === "timeout"
                ? "We couldn't confirm your payment yet. If you completed checkout, it may still be processing — check back shortly."
                : "We could not confirm this payment. No access has been granted."}
            </p>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-[#0A0A0A] text-white font-mono-b uppercase tracking-[0.15em] text-sm px-6 py-3 border-2 border-[#0A0A0A] hover:-translate-y-1 transition-transform duration-200" data-testid="back-home-button">
              <ArrowLeft size={16} strokeWidth={2.5} /> Back to home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
